import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type React from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useProcessImage from '../../../hooks/use-process-image';
import { IdDocImageUploadError } from '../../../types';
import handleFileUpload from '../../../utils/handle-file-upload';
type DraggableInputFieldProps = {
  children?: React.ReactNode;
  hasError?: boolean;
  height: number;
  onComplete: (image: File) => void;
  isLoading?: boolean;
  onUploadError: (errors: IdDocImageUploadError[]) => void;
  allowPdf?: boolean;
};
const DraggableInputField = t0 => {
  const $ = _c(40);
  const {
    children,
    hasError,
    height,
    onComplete,
    isLoading,
    onUploadError,
    allowPdf
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "document-flow.components.photo-upload-buttons"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  const [dragActive, setDragActive] = useState(false);
  const uploadPhotoRef = useRef(undefined);
  let t2;
  if ($[1] !== allowPdf) {
    t2 = {
      allowPdf
    };
    $[1] = allowPdf;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    acceptedFileFormats
  } = useProcessImage(t2);
  let t3;
  if ($[3] !== acceptedFileFormats || $[4] !== onComplete || $[5] !== onUploadError) {
    t3 = ev => {
      ev.preventDefault();
      setDragActive(false);
      if (ev.dataTransfer.items) {
        const files = [];
        [...ev.dataTransfer.items].forEach(item => {
          if (item.kind === "file") {
            const file = item.getAsFile();
            if (file) {
              files.push(file);
            }
          }
        });
        const result = handleFileUpload({
          acceptedFileFormats,
          files
        });
        if (!result.ok) {
          onUploadError(result.errors);
          return;
        }
        onComplete(result.file);
      } else {
        onUploadError([IdDocImageUploadError.unknownUploadError]);
      }
    };
    $[3] = acceptedFileFormats;
    $[4] = onComplete;
    $[5] = onUploadError;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleFileDrop = t3;
  const handleDrag = _temp;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ev_1 => {
      ev_1.preventDefault();
      setDragActive(true);
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleDragEnter = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ev_2 => {
      ev_2.preventDefault();
      setDragActive(false);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleDragLeave = t5;
  let t6;
  if ($[9] !== acceptedFileFormats || $[10] !== onComplete || $[11] !== onUploadError) {
    t6 = ev_3 => {
      const {
        files: files_0
      } = ev_3.target;
      const result_0 = handleFileUpload({
        acceptedFileFormats,
        files: files_0
      });
      if (!result_0.ok) {
        onUploadError(result_0.errors);
        return;
      }
      onComplete(result_0.file);
    };
    $[9] = acceptedFileFormats;
    $[10] = onComplete;
    $[11] = onUploadError;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleImageUpload = t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      uploadPhotoRef.current?.click();
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleUpload = t7;
  const t8 = !!isLoading;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleUpload();
      }
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = hasError && "bg-secondary border-solid border-error";
  const t11 = isLoading && "bg-neutral pointer-events-none *:pointer-events-none";
  const t12 = dragActive && "bg-active *:pointer-events-none";
  const t13 = !hasError && (dragActive ? "border-secondary" : "border-primary");
  let t14;
  if ($[15] !== t10 || $[16] !== t11 || $[17] !== t12 || $[18] !== t13) {
    t14 = cx("flex flex-col justify-center items-center border border-dashed rounded cursor-pointer", t10, t11, t12, t13, "active:border-secondary");
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const t15 = `${height}px`;
  let t16;
  if ($[20] !== t15) {
    t16 = {
      minHeight: t15
    };
    $[20] = t15;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== children || $[23] !== handleFileDrop || $[24] !== hasError || $[25] !== t14 || $[26] !== t16 || $[27] !== t8) {
    t17 = <div role="button" tabIndex={0} onDrop={handleFileDrop} onDragOver={handleDrag} onDragLeave={handleDragLeave} onDragEnter={handleDragEnter} data-error={hasError} data-loading={t8} onClick={handleUpload} onKeyDown={t9} className={t14} style={t16}>{children}</div>;
    $[22] = children;
    $[23] = handleFileDrop;
    $[24] = hasError;
    $[25] = t14;
    $[26] = t16;
    $[27] = t8;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== acceptedFileFormats) {
    t18 = acceptedFileFormats.join(",");
    $[29] = acceptedFileFormats;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t) {
    t19 = t(_temp2);
    $[31] = t;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== handleImageUpload || $[34] !== t18 || $[35] !== t19) {
    t20 = <input ref={uploadPhotoRef as React.RefObject<HTMLInputElement | null>} type="file" accept={t18} onChange={handleImageUpload} aria-label={t19} className="hidden" />;
    $[33] = handleImageUpload;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t17 || $[38] !== t20) {
    t21 = <div className="gap-6">{t17}{t20}</div>;
    $[37] = t17;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  return t21;
};
export default DraggableInputField;
function _temp(ev_0) {
  ev_0.preventDefault();
}
function _temp2($) {
  return $["upload-file"].title;
}